import { supabase } from '@/lib/supabase';
import { db } from '@/lib/offline-db';

let isSyncing = false;

// ============================================
// Sync-Queue verarbeiten
// Wird aufgerufen wenn die App online ist oder wieder online kommt.
// ============================================

export async function processSyncQueue(): Promise<void> {
  if (isSyncing || !navigator.onLine) return;

  const pending = await db.sync_queue.orderBy('queued_at').toArray();
  if (pending.length === 0) return;

  isSyncing = true;

  try {
    for (const action of pending) {
      try {
        if (action.action === 'create') {
          // Beim Create: Temp-ID entfernen, Supabase erzeugt echte ID
          const { id: _tempId, ...payloadWithoutId } = action.payload as Record<string, unknown>;
          const { error } = await supabase
            .from('properties')
            .insert(payloadWithoutId);
          if (error) throw error;

        } else if (action.action === 'update') {
          // Conflict Resolution: Last-Write-Wins via updated_at
          const serverUpdatedAt = await getServerUpdatedAt(action.record_id);
          const localUpdatedAt = (action.payload as Record<string, unknown>).updated_at as string | undefined;

          if (serverUpdatedAt && localUpdatedAt && serverUpdatedAt > localUpdatedAt) {
            // Server ist neuer → lokale Änderung überspringen
            console.warn(`[Sync] Conflict: server newer for ${action.record_id}, skipping local update`);
          } else {
            const { error } = await supabase
              .from('properties')
              .update(action.payload)
              .eq('id', action.record_id);
            if (error) throw error;
          }

        } else if (action.action === 'delete') {
          const { error } = await supabase
            .from('properties')
            .delete()
            .eq('id', action.record_id);
          if (error) throw error;
        }

        // Erfolgreich → aus Queue entfernen
        if (action.id !== undefined) {
          await db.sync_queue.delete(action.id);
        }

      } catch (actionError) {
        // Netzwerkfehler → Queue bleibt, beim nächsten Online-Event retry
        if (!navigator.onLine) break;
        // Andere Fehler (z.B. RLS-Fehler) → überspringen um Queue nicht zu blockieren
        console.error(`[Sync] Failed to sync action ${action.action} for ${action.record_id}:`, actionError);
        if (action.id !== undefined) {
          await db.sync_queue.delete(action.id);
        }
      }
    }
  } finally {
    isSyncing = false;
  }
}

async function getServerUpdatedAt(propertyId: string): Promise<string | null> {
  try {
    const { data } = await supabase
      .from('properties')
      .select('updated_at')
      .eq('id', propertyId)
      .single();
    return data?.updated_at ?? null;
  } catch {
    return null;
  }
}

// Anzahl ausstehender Sync-Aktionen (für UI-Badge)
export async function getSyncQueueCount(): Promise<number> {
  return db.sync_queue.count();
}
